/* Nationwide CareMatters Florida Insurance Application */
import { sharedOneAmericaSections } from './oneAmericaAppSharedSections';
import { sharedOneAmericaQuestions } from './oneAmericaAppSharedQuestions';
import { oneAmericaSharedDefs } from './oneAmericaSharedDefs';
import { oneAmericaPreApplicationFormDefinition } from '@shared/policy-application/oneAmericaPreApplicationFormDef';

const policyProductFundingQuestion = sharedOneAmericaQuestions.policyProductFundingSelection;

const questions: Record<string, PolicyFormQuestion> = {
  ...oneAmericaPreApplicationFormDefinition.questions,
  ...sharedOneAmericaQuestions,

  /* Section 6 - Policy Selections */
  policyProductFundingSelection: {
    ...policyProductFundingQuestion,
    options: [
      ...(policyProductFundingQuestion.options
        ? policyProductFundingQuestion.options.filter(el => el.id !== 'annuity-funding')
        : []),
      {
        id: 'annuity-funding',
        value:
          'Annuity Funding Whole Life (application I-31157 (FL) must also be completed) (if selected, proceed to #3)',
      },
    ],
  },

  /* Section 9 - Information Regarding other Coverage */
  otherHealthInsurancePoliciesSold: {
    id: 'other-health-insurance-policies-sold',
    questionRef: 'otherHealthInsurancePoliciesSold',
    title: 'List any other health insurance policies you have sold to any proposed insured',
    type: 'text',
    required: false,
  },
  policiesSoldInForce: {
    id: 'policies-sold-in-force',
    questionRef: 'policiesSoldInForce',
    title: 'List policies sold that are still in force',
    type: 'text',
    required: false,
  },
  policiesSoldPast5YearsNotInForce: {
    id: 'policies-sold-past-5-years-not-in-force',
    questionRef: 'policiesSoldPast5YearsNotInForce',
    title: 'List policies sold in the past 5 years that are no longer in force',
    type: 'text',
    required: false,
  },

  /* Section 10 - Special Request / Additional Information */
  illustrationNotUsedInSale: {
    id: 'illustration-not-used-in-sale',
    questionRef: 'illustrationNotUsedInSale',
    title: 'I certify that an illustration was not used in the sale of this policy',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Medical Questions A - During past 10 years */
  medicalAidsFirstInsured: {
    id: 'medical-aids-first-insured',
    questionRef: 'medicalAidsFirstInsured',
    title:
      'Has any person proposed for insurance tested positive for exposure to the Human Immunodeficiency Virus (HIV) infection or been diagnosed by a licensed health care practitioner as having Acquired Immune Deficiency Syndrome (AIDS), AIDS-Related Complex (ARC), or AIDS caused by HIV infection or sickness or condition derived by such infection?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAidsSecondInsured: {
    id: 'medical-aids-second-insured',
    questionRef: 'medicalAidsSecondInsured',
    title:
      'Has any person proposed for insurance tested positive for exposure to the Human Immunodeficiency Virus (HIV) infection or been diagnosed by a licensed health care practitioner as having Acquired Immune Deficiency Syndrome (AIDS), AIDS-Related Complex (ARC), or AIDS caused by HIV infection or sickness or condition derived by such infection?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Medical Questions B - During past 5 years */
  medicalDiagnosticTestsFirstInsured: {
    id: 'medical-diagnostic-tests-first-insured',
    questionRef: 'medicalDiagnosticTestsFirstInsured',
    title:
      'Been advised by a member of the medical profession to have any diagnostic test, hospitalization, or surgery which has not been completed? Do not include tests related to Human Immunodeficiency Virus (HIV) or Aquired Immune Deficiency Syndrome (AIDS)',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDiagnosticTestsSecondInsured: {
    id: 'medical-diagnostic-tests-second-insured',
    questionRef: 'medicalDiagnosticTestsSecondInsured',
    title:
      'Been advised by a member of the medical profession to have any diagnostic test, hospitalization, or surgery which has not been completed? Do not include tests related to Human Immunodeficiency Virus (HIV) or Aquired Immune Deficiency Syndrome (AIDS)',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Long-Term Care Insurance Personal Worksheet */
  ltcPremiumPolicyFormNumbers: {
    id: 'ltc-premium-policy-form-numbers',
    questionRef: 'ltcPremiumPolicyFormNumbers',
    title: 'Policy Form Numbers',
    type: 'text',
    required: false,
  },
  annualPremiumPaymentMethod: {
    id: 'annual-premium-payment-method',
    questionRef: 'annualPremiumPaymentMethod',
    title: "How will you pay for each year's premium?",
    type: 'multiselect',
    required: false,
    options: [
      { id: 'from-my-income', value: 'From My Income' },
      { id: 'from-my-savings', value: 'From My Savings/Investments' },
      { id: 'my-family-will-pay', value: 'My Family Will Pay' },
    ],
  },
  ltcPayForFutureCosts: {
    id: 'ltc-pay-for-future-costs',
    questionRef: 'ltcPayForFutureCosts',
    title:
      'Have you considered how you will pay for the difference between future costs and your daily benefit amount?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'from-my-income', value: 'From My Income' },
      { id: 'from-my-savings', value: 'From My Savings/Investments' },
      { id: 'my-family-will-pay', value: 'My Family Will Pay' },
    ],
    conditional: {
      visible: {
        field: 'ltcBuyInflationProtection',
        value: 'no',
        operator: 'equals',
      },
    },
  },

  /* Representative's Statement */
  representativeAgentInfo: {
    id: 'representative-agent-info',
    questionRef: 'representativeAgentInfo',
    title: 'Representative Information',
    type: 'table',
    required: true,
    minRows: 1,
    maxRows: 3,
    columns: [
      {
        id: 'name',
        title: 'Name of Agent',
        type: 'text',
        required: true,
      },
      {
        id: 'email',
        title: 'Email of Agent',
        type: 'text',
        required: true,
      },
      {
        id: 'percentage',
        title: '%',
        type: 'number',
        required: true,
      },
      {
        id: 'code',
        title: "Agent's SL Code",
        type: 'text',
        required: true,
      },
      {
        id: 'id-number',
        title: 'Florida License Identification Number',
        type: 'text',
        required: true,
      },
    ],
  },

  // Notice Regarding Replacement
  replacementComparativeInfo: {
    id: 'replacement-comparative-info',
    questionRef: 'replacementComparativeInfo',
    title:
      'Florida regulations give you the right to receive a written Comparative Information Form which summarizes your policy values. Indicate whether or not you wish to receive a Comparative Information Form from the proposed company and your existing insurer or insurers by placing your initials in the appropriate box below.',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  replacementAgentName: {
    id: 'replacement-agent-name',
    questionRef: 'replacementAgentName',
    title: 'Agent’s Name',
    type: 'text',
    required: true,
  },

  replacementAgentAddress: {
    id: 'replacement-agent-address',
    questionRef: 'replacementAgentAddress',
    title: 'Agent’s Address',
    type: 'text',
    required: true,
  },

  replacementAgentCompany: {
    id: 'replacement-agent-company',
    questionRef: 'replacementAgentCompany',
    title: 'Agent’s Company',
    type: 'text',
    required: true,
  },

  replacementNoticeTable: {
    id: 'replacement-notice-table',
    questionRef: 'replacementNoticeTable',
    title: 'Information on Policies which may be replaced:',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 3,
    columns: [
      {
        id: 'company',
        title: 'Company Name',
        type: 'text',
        required: true,
      },
      {
        id: 'policy-number',
        title: 'Policy Number',
        type: 'text',
        required: true,
      },
      {
        id: 'name-of-insured',
        title: 'Name of Insured',
        type: 'text',
        required: true,
      },
    ],
  },
};

export const oneAmericaAssetCareFLFormDef: PolicyFormDefinition = {
  requiredParameters: oneAmericaSharedDefs.requiredParameters,
  sections: [
    ...oneAmericaPreApplicationFormDefinition.sections,
    sharedOneAmericaSections.firstProposedInsured,
    sharedOneAmericaSections.secondProposedInsured,
    sharedOneAmericaSections.owner,
    sharedOneAmericaSections.jointOwner,
    sharedOneAmericaSections.beneficiary,
    sharedOneAmericaSections.policySelections,
    sharedOneAmericaSections.annualIncome,
    sharedOneAmericaSections.protectionAgainstLapse,
    /* 9 ▸ Information Regarding Other Coverage ------------------------------------------------------- */
    {
      id: 'other-coverage',
      title: 'Information Regarding Other Coverage',
      subsections: [
        {
          id: 'ltc-coverage',
          title: 'Long-Term Care Coverage',
          questionGroups: [
            {
              id: 'current-ltc',
              format: 'default',
              questions: ['coverageHasOtherLtc', 'coverageHadLtcLastYear'],
            },
            {
              id: 'ltc-details',
              format: 'grid',
              questions: ['coverageLtcCompanyName', 'coverageLtcLapseDate'],
              conditional: {
                visible: {
                  field: 'coverageHadLtcLastYear',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'insurance-status',
              format: 'default',
              questions: ['coverageHasExistingInsurance'],
            },
            {
              id: 'insurance-details',
              format: 'default',
              questions: ['coverageExistingInsuranceDetails'],
              conditional: {
                visible: {
                  operator: 'or',
                  conditions: [
                    {
                      field: 'coverageHasOtherLtc',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHadLtcLastYear',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHasExistingInsurance',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              },
            },
            {
              id: 'other-policies-sold',
              format: 'default',
              questions: ['otherHealthInsurancePoliciesSold'],
            },
            {
              id: 'policies-in-force',
              format: 'default',
              questions: ['policiesSoldInForce'],
            },
            {
              id: 'past-policies-not-in-force',
              format: 'default',
              questions: ['policiesSoldPast5YearsNotInForce'],
            },
            {
              id: 'medicaid-status',
              format: 'default',
              questions: ['coverageEligibleMedicaid'],
            },
            {
              id: 'replacement-info',
              format: 'default',
              questions: ['coverageReplacingPolicy'],
            },
            {
              id: 'pending-status',
              format: 'default',
              questions: ['coverageApplicationPending'],
            },
            {
              id: 'pending-details',
              format: 'grid',
              questions: ['coveragePendingCompany', 'coveragePendingAmount'],
              conditional: {
                visible: {
                  field: 'coverageApplicationPending',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'settlement-status',
              format: 'default',
              questions: ['coverageSoldLifeSettlement'],
            },
            {
              id: 'settlement-details',
              format: 'grid',
              questions: ['coverageSettlementCompany', 'coverageSettlementAmount'],
              conditional: {
                visible: {
                  field: 'coverageSoldLifeSettlement',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.specialRequest,
    sharedOneAmericaSections.acknowledgement,
    sharedOneAmericaSections.substituteW9,
    /* 13 ▸ Representative's Statement ------------------------------------------------------- */
    {
      id: 'representative-statement',
      title: "Representative's Statement",
      subsections: [
        {
          id: 'replacement-knowledge',
          title: 'Replacement Information',
          questionGroups: [
            {
              id: 'replacement-status',
              format: 'default',
              questions: ['representativeReplacementKnowledge'],
            },
          ],
        },
        {
          id: 'identification-info',
          title: 'Identification Information',
          questionGroups: [
            {
              id: 'witness-identification',
              format: 'grid',
              questions: [
                'representativeWitnessedSignatures',
                'representativeIdentificationMethod',
              ],
            },
            {
              id: 'relationship-details',
              format: 'default',
              questions: ['representativeRelationshipExplanation', 'representativeFormerNames'],
            },
          ],
        },
        {
          id: 'evaluation-info',
          title: 'Application Evaluation',
          questionGroups: [
            {
              id: 'other-applications',
              format: 'default',
              questions: ['representativeEvaluateWithOthers'],
            },
            {
              id: 'other-applicant-details',
              format: 'grid',
              questions: [
                'representativeOtherApplicantName',
                'representativeOtherApplicantRelationship',
              ],
              conditional: {
                visible: {
                  field: 'representativeEvaluateWithOthers',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
        {
          id: 'agent-illustration-certification',
          title: 'Agent Illustration Certification',
          questionGroups: [
            {
              id: 'illustration-certification',
              format: 'default',
              questions: ['illustrationNotUsedInSale'],
            },
          ],
        },
        {
          id: 'agent-info',
          title: 'Representative Information',
          questionGroups: [
            {
              id: 'agent-details',
              format: 'default',
              questions: ['representativeAgentInfo'],
            },
          ],
        },
        {
          id: 'contact-info',
          title: 'Contact Information',
          questionGroups: [
            {
              id: 'office-contact',
              format: 'grid',
              questions: [
                'representativeContactName',
                'representativeContactPhone',
                'representativeContactEmail',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.healthPreQuestionSection,
    sharedOneAmericaSections.medicalHealthInfoFirstProposedInsured,
    sharedOneAmericaSections.medicalHealthInfoSecondProposedInsured,
    sharedOneAmericaSections.usPatriotAct,
    sharedOneAmericaSections.authDisclosurePersonalInfo,
    sharedOneAmericaSections.ltcPremiumAnnualSingle,
    sharedOneAmericaSections.ltcPremiumAnnualSingleRider,
    sharedOneAmericaSections.rofVia1035Exchange,
    sharedOneAmericaSections.electronicPremiumPaymentAuthorization,
    /* 23 ▸ Notice Regarding Replacement ------------------------------------------------------- */
    {
      id: 'notice-regarding-replacement',
      title: 'Notice regarding replacement',
      subsections: [
        {
          id: 'insurance-policy-replacement',
          title: 'Notice To Applicant Regarding Replacement Of Life Insurance',
          questionGroups: [
            {
              id: 'insurance-policy-comparative-group',
              format: 'default',
              questions: ['replacementComparativeInfo'],
            },
            {
              id: 'insurance-policy-agent-info-group',
              format: 'grid',
              questions: [
                'replacementAgentName',
                'replacementAgentAddress',
                'replacementAgentCompany',
              ],
            },
            {
              id: 'insurance-policy-replacement-group',
              format: 'default',
              questions: ['replacementNoticeTable'],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'policyReplacement',
          operator: 'equals',
          value: 'yes',
        },
      },
    },
    /* 23 ▸ LTC Insurance Personal Worksheet ------------------------------------------------------- */
    {
      id: 'ltc-insurance-personal-worksheet',
      title: 'Long-Term Care Insurance Personal Worksheet',
      subsections: [
        /* Premium Information */
        {
          id: 'ltc-worksheet-premium-info',
          title: 'Premium Information',
          questionGroups: [
            {
              id: 'ltc-worksheet-premium-form-numbers',
              format: 'default',
              questions: ['ltcPremiumPolicyFormNumbers'],
            },
            {
              id: 'ltc-worksheet-premium-info-group',
              format: 'default',
              questions: ['ltcPremiumPerMonth', 'ltcPremiumPerYear', 'ltcPremiumOneTime'],
            },
          ],
        },

        /* Questions About Your Income */
        {
          id: 'ltc-worksheet-income',
          title: 'Questions About Your Income',
          questionGroups: [
            {
              id: 'ltc-worksheet-annual-premium-payment',
              format: 'default',
              questions: ['annualPremiumPaymentMethod'],
            },
            {
              id: 'ltc-worksheet-income-overview',
              format: 'default',
              questions: [
                'ltcHouseholdIncome',
                'ltcIncomeChangeExpectation',
                'ltcBuyInflationProtection',
                'ltcPayForFutureCosts',
                'ltcPayDuringElimination',
                'ltcAssetsWorth',
                'ltcAssetsChangeExpectation',
              ],
            },
            {
              id: 'ltc-worksheet-disclosure-group',
              format: 'default',
              questions: ['ltcDisclosureStatement', 'ltcAgreeReviewed'],
            },
            {
              id: 'ltc-worksheet-agent-ack',
              format: 'grid',
              questions: [
                'ltcAgentExplained',
                'ltcAgentPrintedName',
                'personalWorksheetNotSuitable',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.preInterviewWorksheet,
    sharedOneAmericaSections.temporaryInsuranceAgreement,
    sharedOneAmericaSections.w4p,
    sharedOneAmericaSections.alcoholUseSupplement,
    sharedOneAmericaSections.aviationSupplement,
    sharedOneAmericaSections.drugUseSupplement,
    sharedOneAmericaSections.armedForcesSupplement,
    sharedOneAmericaSections.avocationSupplement,
    sharedOneAmericaSections.foreignNationalsTravelSupplement,
    sharedOneAmericaSections.financialInstitutionDisclosure,
    sharedOneAmericaSections.assignment1035Exchange,
    sharedOneAmericaSections.acceptance1035Exchange,
    sharedOneAmericaSections.section1035PartialTransfer,
    sharedOneAmericaSections.powerOfAttorneyAffidavit,
    sharedOneAmericaSections.signatureAuthorityEntity,
  ],
  questions: {
    ...questions,
  },
};
